import { useEffect, useRef } from 'react';
import { useAuthStore } from '../store/auth-store.js';
import { useAppLockStore } from '../store/app-lock-store.js';
import { isNativePlatform } from '../lib/platform.js';

/** 30-minute inactivity window — CLAUDE.md "Session" rules. */
export const INACTIVITY_LIMIT_MS = 30 * 60 * 1000;

/** Re-arm the timer at most this often so input bursts don't churn timers. */
const REARM_THROTTLE_MS = 30 * 1000;

const ACTIVITY_EVENTS: readonly (keyof WindowEventMap)[] = [
  'pointerdown',
  'keydown',
  'wheel',
  'touchstart',
];

/**
 * Watches user activity while authenticated. When the inactivity window
 * elapses: on web the user is logged out (refresh cookie cleared
 * server-side); on native the app locks in place — the session survives,
 * the UI doesn't, until the user re-authenticates (app-lock screen).
 *
 * Mounted once in AppShell, which only renders for authenticated users.
 */
export function useInactivityGuard(): void {
  const status = useAuthStore((s) => s.status);
  const lastActivityRef = useRef<number>(Date.now());

  useEffect(() => {
    if (status !== 'authenticated') return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastArm = 0;
    lastActivityRef.current = Date.now();

    const expire = (): void => {
      if (isNativePlatform()) {
        useAppLockStore.getState().lock();
        // Re-arm so a locked-but-untouched app re-locks cleanly after unlock.
        lastActivityRef.current = Date.now();
        arm();
      } else {
        void useAuthStore.getState().logout();
      }
    };

    const arm = (): void => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(expire, INACTIVITY_LIMIT_MS);
    };

    const onActivity = (): void => {
      lastActivityRef.current = Date.now();
      const now = Date.now();
      if (now - lastArm < REARM_THROTTLE_MS) return;
      lastArm = now;
      arm();
    };

    // Background tabs throttle timers, so a returning tab re-checks the
    // wall clock and expires immediately if the window already elapsed.
    const onVisibility = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivityRef.current >= INACTIVITY_LIMIT_MS) {
        expire();
      } else {
        onActivity();
      }
    };

    arm();
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (timer) clearTimeout(timer);
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity);
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [status]);
}
